import { Box } from '@mui/material';
import { Fragment } from 'react';
import { useRecoilValue } from 'recoil';
import DNDArea from '~/components/DicomLoad/LocalLoad/DNDArea';
import FilesProcessed from '~/components/DicomLoad/LocalLoad/FilesProcessed';
import Toolbar from '~/components/toolbar';
import OperationsToolbar from '~/components/toolbar/OperationsToolbar/OperationsToolbar';
import DicomSourcesModal from '~/containers/patients-modal/DicomSourcesModal';
import ExportDatasetModal from '~/containers/viewer/explorer-sidebar/Export/ExportDatasetModal';
import Viewport from '~/containers/viewer/viewport';
import BackendWorkflows from '~/containers/workflows/BackendWorkflows';
import ConnectorWorkflows from '~/containers/workflows/ConnectorWorkflows';
import { useBackendJobOrchestrator } from '~/hooks/workflows/backend/useBackendJobOrchestrator';
import { useConnectorJobOrchestrator } from '~/hooks/workflows/connector/useConnectorJobOrchestrator';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import AppConfig from '~/utils/appconfig';

/**
 * Runs the backend job orchestrator for standalone (non-connector) mode.
 * Extracted to its own component so it can be conditionally rendered.
 */
function BackendJobOrchestratorProvider() {
    useBackendJobOrchestrator();
    return null;
}

/**
 * Runs the connector job orchestrator when connector mode is active
 * outside a pre-established session (SessionLayout owns the orchestrator
 * for the session-scoped entry point).
 */
function ConnectorJobOrchestratorProvider() {
    useConnectorJobOrchestrator();
    return null;
}

/**
 * Works as a overlay layer for viewer related data
 * @returns Head-up display to show on top of Viewport
 */
function ViewportHUD() {
    const sessionData = useRecoilValue(sessionDataAtom);
    const isConnectorMode = AppConfig.isConnectorMode;

    return (
        <Box width="100%" height="100%" overflow="hidden">
            <DNDArea />
            {!sessionData && <DicomSourcesModal />}
            <ExportDatasetModal />
            {isConnectorMode ? <ConnectorWorkflows /> : <BackendWorkflows />}
        </Box>
    );
}

export default function Viewer() {
    const sessionData = useRecoilValue(sessionDataAtom);
    const isConnectorMode = AppConfig.isConnectorMode;

    return (
        <Fragment>
            {!isConnectorMode && <BackendJobOrchestratorProvider />}
            {isConnectorMode && !sessionData && (
                <ConnectorJobOrchestratorProvider />
            )}
            <Box
                position="relative"
                width="100%"
                height="100%"
                sx={{ userSelect: 'none' }}
            >
                <Viewport />
                <ViewportHUD />
                <Toolbar />
                <OperationsToolbar />
            </Box>
            {!sessionData && <FilesProcessed />}
        </Fragment>
    );
}
